/**
 * The Preact ↔ Mapbox crossing, declared once.
 *
 * `trip-map` and `globe-view` are the last custom elements in the app, and
 * everything that crosses to them is a string: an event name, or a JSON payload
 * in an HTML attribute. Both sides used to restate those shapes by hand — the
 * element parsing one interface, the island casting `CustomEvent<{...}>` at the
 * other end, and `types/web-components.d.ts` naming the attributes a third
 * time. A renamed field was a blank map, never a type error.
 *
 * This module is the single declaration. It is free of Mapbox, Lit and Preact
 * so both sides can import it: the elements bundle it into
 * `static/components-map.js`, and islands take it without dragging 489 KB of
 * Mapbox into their graph.
 *
 * The DOM half of the crossing — waiting for a map, subscribing to its events,
 * serializing the attributes — lives in `./bridge.ts`.
 */

// -----------------------------------------------------------------------------
// Attribute payloads
// -----------------------------------------------------------------------------

/**
 * A JSON payload carried in an HTML attribute.
 *
 * Erased to `string` — an attribute is a string and nothing can change that.
 * The parameter is documentation with a compiler behind it: the attribute
 * declarations in `types/web-components.d.ts` are written in terms of these,
 * so the payload a map element parses and the payload a page serializes are
 * named by the same type.
 */
export type SerializedJson<T> = string

/** A POI as the map renders it: a marker, a popup, and a point for the bounds. */
export interface MapPoi {
  id: string
  type: string
  /** Numeric-as-string is what Postgres `numeric` gives back through JSON. */
  lat: number | string | null
  lng: number | string | null
  /**
   * The compass heading the photographer was facing, degrees clockwise from
   * north. Backfilled from EXIF by `0022_poi_camera_fields.sql`; the Trip
   * Player turns the camera to it so the map frame agrees with the photo.
   */
  dir?: number | string | null
  /** Capture time, ISO 8601 UTC. Fixes where the sun was — see `camera.ts`. */
  took?: string | null
  /** Derived camera hints, attached server-side — see `PoiCamHints`. */
  cam?: PoiCamHints | null
  name?: string | null
  notes?: string | null
  rating?: number | null
  tags?: string[]
  signed_thumbnail?: string | null
  signed_optimized?: string | null
  signed_original?: string | null
  signed_video_thumbnail?: string | null
}

/**
 * Facts about a photo that only its EXIF knows, lifted server-side so the map
 * never has to parse a raw EXIF blob.
 *
 * Deliberately facts, not decisions: what the lens was, not what the camera
 * should do about it. The policy that turns these into a camera lives in
 * `lib/map/camera.ts`, where it is testable on its own.
 */
export interface PoiCamHints {
  /**
   * 35mm-equivalent focal length, in millimetres. Stands in for the photo's
   * field of view, which is what the map camera is trying to echo.
   */
  f35?: number | null
  /**
   * When the shutter actually opened, ISO 8601. Distinct from `took`, which an
   * owner can edit — see `poiCamHints`. Used only to place the sun.
   */
  at?: string | null
}

export interface MapLineString {
  type: "LineString"
  coordinates: [number, number][]
}

export interface MapMultiLineString {
  type: "MultiLineString"
  coordinates: [number, number][][]
}

/** A trip's track, in either GeoJSON shape Postgres hands back. */
export type MapRoute = MapLineString | MapMultiLineString

/** A trip as the globe renders it: one marker, positioned from route or photos. */
export interface MapTrip {
  id: string
  title: string
  happened?: string | null
  route?: MapRoute | null
  cover_thumbnail?: string | null
  photo_latlngs?: Array<{ lat: number; lng: number }>
  lat?: number | null
  lng?: number | null
  tags?: string[]
}

/** What `trip-navigator-control` steps through — id and title, nothing more. */
export interface MapTripSummary {
  id: string
  title: string
}

/** `<trip-map>`'s attributes, by the names the element declares. */
export interface TripMapAttrs {
  token: string
  pois: SerializedJson<MapPoi[]>
  route: SerializedJson<MapRoute>
  height: string
  "style-name": string
  "editable-poi-id": string
  /** POIs that widen the initial viewport without drawing a marker. */
  "bounds-pois": SerializedJson<MapPoi[]>
}

/** `<globe-view>`'s attributes, by the names the element declares. */
export interface GlobeViewAttrs {
  token: string
  trips: SerializedJson<MapTrip[]>
  height: string
  "style-name": string
}

/**
 * What every `MapControl` element takes.
 *
 * `position` is the only one: a control decides nothing else from markup, and
 * nothing positions it from outside the map — `addControl` places it.
 */
export interface MapControlAttrs {
  position: string
}

/** `<trip-navigator-control>`'s attributes. */
export interface TripNavigatorAttrs extends MapControlAttrs {
  trips: SerializedJson<MapTripSummary[]>
}

// -----------------------------------------------------------------------------
// Events
// -----------------------------------------------------------------------------

/** A camera move, carrying nothing — the fact that it happened is the payload. */
export type MapMoveDetail = Record<string, never>

/**
 * Which gesture a viewer used on the camera.
 *
 * The distinction the Trip Player runs on: a pan takes the camera off the
 * subject, so the run stops and waits there; a zoom, a rotate or a pitch keeps
 * the same subject in frame and is the viewer adjusting the shot, so the run
 * carries on underneath it. Named after Mapbox's own `dragstart` /
 * `zoomstart` / `rotatestart` / `pitchstart`, which is where each one is read.
 */
export type MapGesture = "pan" | "zoom" | "rotate" | "pitch"

/** A camera move a viewer started, and which gesture started it. */
export interface MapGestureDetail {
  kind: MapGesture
}

/** A place on the map, with no identity attached. */
export interface MapPointDetail {
  lat: number
  lng: number
}

/** A place on the map that is some record — a POI, a trip. */
export interface MapItemDetail extends MapPointDetail {
  id: string
}

/**
 * Every event a map element dispatches, with the payload it carries.
 *
 * Adding an event here and nowhere else is the point: `onMapEvent` derives its
 * handler type from this map, so a misspelled name has no overload and a wrong
 * field has no property.
 */
export interface MapEventDetailMap {
  /** A marker was clicked. */
  "poi-focused": MapItemDetail
  /** A trip's marker was clicked on the globe. */
  "trip-focused": MapItemDetail
  /** A long-press, or the picker control's confirm. */
  "gps-picked": MapPointDetail
  /** The editable marker was dragged to a new place. */
  "poi-dragged": MapItemDetail
  /** The editable POI had no coordinates and the map was clicked. */
  "poi-placed": MapItemDetail
  /** `trip-navigator-control` stepped to another trip. */
  "trip-selected": { tripId: string }
  /** The style switcher applied a new basemap. */
  "style-change": { style: string }
  /**
   * The Mapbox instance is built and its controls are mounted. `map` is a
   * `mapboxgl.Map`, typed loosely so this module stays Mapbox-free.
   */
  "map-ready": { map: unknown }
  /**
   * A camera move finished — a faithful mirror of Mapbox's `moveend`, fired
   * for programmatic flights and user gestures alike. The Trip Player gates
   * its dwell on this, so it must not be synthesized: a fabricated settle
   * would advance the playhead while the camera flies somewhere else.
   */
  "move-end": MapMoveDetail
  /**
   * A camera move began from a viewer's gesture — raised from Mapbox's
   * `dragstart` / `zoomstart` / `rotatestart` / `pitchstart`, and only when
   * the event carries an `originalEvent`, which is what tells a thumb from a
   * programmatic flight. `kind` says which gesture it was, because the Trip
   * Player stops for a pan and plays on through the other three.
   */
  "user-move": MapGestureDetail
}

export type MapEventName = keyof MapEventDetailMap

/**
 * The event names, as values.
 *
 * `just test::rules` rejects a map event name written as a string literal
 * outside `src/lib/map/`, so this object is how both sides say them.
 */
export const MAP_EVENT = {
  poiFocused: "poi-focused",
  tripFocused: "trip-focused",
  gpsPicked: "gps-picked",
  poiDragged: "poi-dragged",
  poiPlaced: "poi-placed",
  tripSelected: "trip-selected",
  styleChange: "style-change",
  mapReady: "map-ready",
  moveEnd: "move-end",
  userMove: "user-move",
} as const satisfies Record<string, MapEventName>

/**
 * Build a map event. Always bubbling and composed — a map element may sit
 * inside a shadow root, and every listener is on an ancestor.
 */
export function mapEvent<K extends MapEventName>(
  name: K,
  detail: MapEventDetailMap[K],
): CustomEvent<MapEventDetailMap[K]> {
  return new CustomEvent(name, { detail, bubbles: true, composed: true })
}

// -----------------------------------------------------------------------------
// Deserializers
// -----------------------------------------------------------------------------

/**
 * Parse an attribute payload, or fall back.
 *
 * An unparseable attribute is a rendering bug on the page that wrote it, not
 * something the map can recover from — so it degrades to the fallback rather
 * than throwing inside a Lit lifecycle callback and taking the map with it.
 */
function parseJson<T>(json: string, fallback: T): T {
  if (!json) return fallback
  try {
    return JSON.parse(json) as T
  } catch {
    return fallback
  }
}

export function parseMapPois(json: string): MapPoi[] {
  return parseJson<MapPoi[]>(json, [])
}

export function parseMapTrips(json: string): MapTrip[] {
  return parseJson<MapTrip[]>(json, [])
}

export function parseMapTripSummaries(json: string): MapTripSummary[] {
  return parseJson<MapTripSummary[]>(json, [])
}

/**
 * Parse a route into the one shape the renderers handle.
 *
 * A bare `LineString` is widened to a single-segment `MultiLineString` so
 * callers have one case, not two.
 */
export function parseMapRoute(json: string): MapMultiLineString | null {
  const parsed = parseJson<MapRoute | null>(json, null)
  if (!parsed) return null
  if (parsed.type === "MultiLineString") return parsed
  if (parsed.type === "LineString") {
    return { type: "MultiLineString", coordinates: [parsed.coordinates] }
  }
  return null
}

/** A POI's coordinates as numbers, or null when it has no place on the map. */
export function poiLngLat(poi: MapPoi): { lat: number; lng: number } | null {
  if (poi.lat === null || poi.lng === null) return null
  const lat = parseFloat(String(poi.lat))
  const lng = parseFloat(String(poi.lng))
  if (isNaN(lat) || isNaN(lng)) return null
  return { lat, lng }
}
